/**
 * Share Tokens tab in the Company workspace.
 *
 * J-C4 + J-C5: live form wired to backend APIs.
 *  - Issue form: test selector, expiry, usage limit, embed toggle
 *  - Token list with status badges
 *  - Embed mode: allowed domains + snippet display
 *  - Copy-to-clipboard for token URL
 *  - Revoke action
 */

import { useState, useCallback, useEffect, useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Shell } from '@/components/shell';
import { Button, Card, Input } from '@/components/ui';
import { deriveStatus, type ShareTokenItem } from '../../state/share-token/types.ts';
import { StatusBadge } from '../../components/share-token/StatusBadge.tsx';
import { apiFetch } from '@/lib/api-client';

interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export function ShareTokensPage(): ReactElement {
  const params = useParams<{ id: string }>();
  const companyId = params.id ?? '';
  const nowIso = useMemo(() => new Date().toISOString(), []);

  // Token list
  const [tokens, setTokens] = useState<ShareTokenItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listErrorKind, setListErrorKind] = useState<'forbidden' | 'load_failure' | null>(null);

  // Issue form
  const [testId, setTestId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [maxUses, setMaxUses] = useState<number>(1);
  const [isEmbed, setIsEmbed] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState('');
  const [label, setLabel] = useState('');
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issuedToken, setIssuedToken] = useState<ShareTokenItem | null>(null);
  const [isIssuing, setIsIssuing] = useState(false);

  // Copy feedback
  const [copied, setCopied] = useState(false);

  const loadTokens = useCallback(
    async (nextCursor?: string | null) => {
      setIsLoading(true);
      setListError(null);

      try {
        const q = new URLSearchParams();
        if (nextCursor) q.set('cursor', nextCursor);
        q.set('limit', '25');

        const response = await apiFetch(
          `/api/companies/${companyId}/share-tokens?${q.toString()}`,
          { credentials: 'include' },
        );

        if (response.status === 403) {
          setListErrorKind('forbidden');
          setListError('forbidden');
          return;
        }

        if (!response.ok) {
          setListErrorKind('load_failure');
          setListError('Failed to load share tokens. Please try again.');
          return;
        }

        const data = (await response.json()) as {
          ok: boolean;
          data: Page<ShareTokenItem>;
          error?: { code: string; message: string };
        };
        if (!data.ok) {
          if (data.error?.code === 'forbidden') {
            setListErrorKind('forbidden');
            setListError('forbidden');
            return;
          }
          setListErrorKind('load_failure');
          setListError('Failed to load share tokens. Please try again.');
          return;
        }

        setListErrorKind(null);
        if (nextCursor) {
          setTokens((prev) => [...prev, ...data.data.items]);
        } else {
          setTokens(data.data.items);
        }

        setCursor(data.data.next_cursor);
        setHasMore(!!data.data.next_cursor);
      } catch {
        setListErrorKind('load_failure');
        setListError('Failed to load share tokens. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  const handleIssue = async (): Promise<void> => {
    if (!testId.trim()) {
      setIssueError('Test ID is required');
      return;
    }
    setIsIssuing(true);
    setIssueError(null);
    setIssuedToken(null);

    try {
      const body: Record<string, unknown> = {
        test_id: testId.trim(),
        max_uses: maxUses,
        is_embed_anonymous: isEmbed,
        label: label || undefined,
      };
      if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      if (isEmbed && allowedDomains.trim()) {
        body.allowed_embed_domains = allowedDomains
          .split(/[\n,;]+/)
          .map((d) => d.trim())
          .filter((d) => d.length > 0);
      }

      const response = await apiFetch(
        `/api/companies/${companyId}/share-tokens`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
          credentials: 'include',
        },
      );

      const data = (await response.json()) as {
        ok: boolean;
        data?: ShareTokenItem;
        error?: { message: string };
      };

      if (!data.ok) {
        setIssueError(data.error?.message ?? 'Failed to issue token');
        return;
      }

      if (data.data) {
        setIssuedToken(data.data);
        // Refresh token list
        void loadTokens();
        // Reset form
        setTestId('');
        setExpiresAt('');
        setMaxUses(1);
        setLabel('');
      }
    } catch {
      setIssueError('Network error');
    } finally {
      setIsIssuing(false);
    }
  };

  const handleCopy = (text: string): void => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async (tokenId: string): Promise<void> => {
    try {
      await apiFetch(`/api/share-tokens/${tokenId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      void loadTokens();
    } catch {
      // Silently fail — list refresh will surface state
    }
  };

  return (
    <Shell mode="company" densityMode="workspace" companyId={companyId}>
      <div className="mx-auto max-w-page px-page-pad-x py-12">
        <header className="mb-8">
          <p className="font-mono text-mono text-muted-2">SHARE TOKENS</p>
          <h1 className="mt-2 font-sans text-headline font-medium tracking-tight">
            Share Tokens
          </h1>
          <p className="mt-2 max-w-prose font-sans text-body text-muted">
            Issue Share Tokens to candidates. Each Token grants one (or more)
            Attempts at the selected Test.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Issue form */}
          <aside className="lg:col-span-1">
            <Card variant="default" className="p-4 space-y-4">
              <h2 id="issue-form" className="font-sans text-title font-medium">
                Issue a new Token
              </h2>

              <div>
                <label htmlFor="test-id" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Test ID
                </label>
                <Input
                  id="test-id"
                  name="test-id"
                  type="text"
                  placeholder="Enter test ID"
                  value={testId}
                  onChange={(e) => setTestId(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="label-input" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Label (optional)
                </label>
                <Input
                  id="label-input"
                  type="text"
                  placeholder="e.g. Candidate A"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="expires-at" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Expiration
                </label>
                <Input
                  id="expires-at"
                  name="expires-at"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="max-uses" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Usage limit
                </label>
                <Input
                  id="max-uses"
                  name="max-uses"
                  type="number"
                  min={0}
                  value={maxUses}
                  onChange={(e) => setMaxUses(parseInt(e.target.value, 10) || 1)}
                />
              </div>

              {/* Embed toggle */}
              <div className="flex items-center gap-3">
                <input
                  id="embed-toggle"
                  type="checkbox"
                  checked={isEmbed}
                  onChange={(e) => setIsEmbed(e.target.checked)}
                  className="h-4 w-4 rounded border-line-2 text-accent focus:ring-accent"
                />
                <label htmlFor="embed-toggle" className="font-sans text-body text-ink">
                  Embed mode (iframe)
                </label>
              </div>

              {isEmbed && (
                <div>
                  <label htmlFor="allowed-domains" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                    Allowed domains (one per line)
                  </label>
                  <textarea
                    id="allowed-domains"
                    name="allowed-domains"
                    placeholder={'example.com\nsub.example.com'}
                    value={allowedDomains}
                    onChange={(e) => setAllowedDomains(e.target.value)}
                    rows={3}
                    className="w-full rounded border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-200"
                  />
                </div>
              )}

              {issueError && (
                <p className="font-sans text-small text-error" role="alert">{issueError}</p>
              )}

              <Button
                variant="primary"
                type="button"
                onClick={() => void handleIssue()}
                disabled={isIssuing}
                className="w-full"
              >
                {isIssuing ? 'Issuing…' : 'Issue Token'}
              </Button>
            </Card>
          </aside>

          {/* Main content: issued token display + list */}
          <section className="lg:col-span-2 space-y-6">
            {/* Newly issued token display */}
            {issuedToken && (
              <Card variant="default" className="p-4 space-y-3 border-accent/30 bg-accent-l/20">
                <h3 className="font-sans text-title font-medium text-accent">
                  Token issued!
                </h3>
                <div>
                  <p className="font-sans text-small text-muted mb-1">Share URL</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded border border-line bg-card px-3 py-2 font-mono text-xs text-ink break-all">
                      {issuedToken.direct_url}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => handleCopy(issuedToken.direct_url)}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>
                {isEmbed && issuedToken.embed_snippet && (
                  <div>
                    <p className="font-sans text-small text-muted mb-1">Embed snippet</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded border border-line bg-card px-3 py-2 font-mono text-xs text-ink break-all">
                        {issuedToken.embed_snippet}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => handleCopy(issuedToken.embed_snippet ?? '')}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Error state */}
            {listError && listErrorKind === 'forbidden' && (
              <div className="rounded border border-line bg-card p-8 text-center" role="alert">
                <p className="font-sans text-title font-medium text-ink">You don&apos;t have access to this company.</p>
                <p className="mt-2 font-sans text-small text-muted">
                  You are not an active member of this company workspace.
                  Contact your workspace administrator to request access.
                </p>
              </div>
            )}
            {listError && listErrorKind === 'load_failure' && (
              <div className="rounded border border-error/30 bg-error/5 p-4" role="alert">
                <p className="font-sans text-small text-error">
                  Something went wrong loading share tokens.
                </p>
                <Button variant="ghost" size="sm" type="button" onClick={() => void loadTokens()} className="mt-2">
                  Retry
                </Button>
              </div>
            )}

            {/* Token list */}
            <section aria-labelledby="token-list">
              <h2 id="token-list" className="mb-4 font-sans text-title font-medium">
                Issued Tokens
              </h2>
              {isLoading && tokens.length === 0 ? (
                <div className="rounded border border-line bg-card p-8 text-center">
                  <p className="font-sans text-body text-muted">Loading tokens…</p>
                </div>
              ) : tokens.length === 0 ? (
                <div className="rounded border border-line bg-card p-8 text-center">
                  <p className="font-sans text-body text-muted">No tokens yet.</p>
                  <p className="mt-2 font-sans text-small text-muted">
                    Use the form to issue your first Share Token.
                  </p>
                </div>
              ) : (
                <Card variant="default" className="overflow-hidden p-0">
                  <table className="w-full">
                    <thead className="border-b border-line bg-bg-warm">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                          Label / Token
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                          Status
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                          Uses
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                          Expires
                        </th>
                        <th scope="col" className="px-4 py-3 text-right font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tokens.map((t) => (
                        <tr key={t.id} className="border-b border-line last:border-b-0">
                          <td className="px-4 py-3 font-mono text-xs text-ink">
                            {t.label ?? t.token.slice(0, 8)}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={deriveStatus(t, nowIso)} />
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted">
                            {String(t.use_count)} / {t.max_uses === 0 ? '∞' : String(t.max_uses)}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted">
                            {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {deriveStatus(t, nowIso) === 'active' && (
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleCopy(t.direct_url)}
                                >
                                  Copy URL
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  type="button"
                                  onClick={() => void handleRevoke(t.id)}
                                  className="text-error hover:text-error"
                                >
                                  Revoke
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {hasMore && (
                    <div className="border-t border-line px-4 py-3 text-center">
                      <Button variant="ghost" type="button" onClick={() => void loadTokens(cursor)}>
                        Load more
                      </Button>
                    </div>
                  )}
                </Card>
              )}
            </section>
          </section>
        </div>
      </div>
    </Shell>
  );
}
